/**
 * Message templates, as the screen needs them.
 *
 * Every write here goes through Row Level Security as the person doing it:
 * `message_templates` is policed on `templates.manage` for every write and
 * `is_member` alone for reading, so anyone on the team can use a template a
 * manager wrote, and only a manager can change what it says.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { validateTemplateContent, type TemplateChannel } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';

export interface MessageTemplate {
    id: string;
    name: string;
    content: string;
    channel: TemplateChannel;
    isDefault: boolean;
    archivedAt: string | null;
    updatedAt: string;
}

interface TemplateRow {
    id: string;
    name: string;
    content: string;
    channel: TemplateChannel;
    is_default: boolean;
    archived_at: string | null;
    updated_at: string;
}

const fromRow = (r: TemplateRow): MessageTemplate => ({
    id: r.id, name: r.name, content: r.content, channel: r.channel,
    isDefault: r.is_default, archivedAt: r.archived_at, updatedAt: r.updated_at,
});

export function useTemplates(workspaceId: string) {
    const [templates, setTemplates] = useState<MessageTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);
        const { data, error: loadError } = await supabase()
            .from('message_templates')
            .select('id, name, content, channel, is_default, archived_at, updated_at')
            .eq('workspace_id', workspaceId)
            .order('channel').order('name');
        if (issuedFor.current !== workspaceId) return;
        if (loadError) {
            setError('We could not load your templates. Try again in a moment.');
        } else {
            setTemplates((data as TemplateRow[]).map(fromRow));
        }
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    return { templates, loading, error, reload: load };
}

export class TemplateError extends Error {}

const permissionMessage = (message: string): string =>
    /row-level security|permission denied/i.test(message)
        ? 'You do not have permission to manage templates.'
        : 'We could not save that template. Try again in a moment.';

export interface TemplateDraft {
    name: string;
    content: string;
    channel: TemplateChannel;
}

/** Thrown before any network call when the content itself is not usable. */
export function assertUsableTemplate(draft: TemplateDraft): void {
    if (!draft.name.trim()) throw new TemplateError('Give the template a name.');
    if (!draft.content.trim()) throw new TemplateError('The template has no content.');
    const check = validateTemplateContent(draft.content);
    if (!check.ok) {
        throw new TemplateError(
            `Unknown placeholder${check.unknownVariables.length > 1 ? 's' : ''}: `
            + check.unknownVariables.map(v => `{{${v}}}`).join(', '),
        );
    }
}

export async function createTemplate(workspaceId: string, draft: TemplateDraft): Promise<string> {
    assertUsableTemplate(draft);
    const { data, error } = await supabase()
        .from('message_templates')
        .insert({ workspace_id: workspaceId, name: draft.name.trim(), content: draft.content, channel: draft.channel })
        .select('id')
        .single();
    if (error) throw new TemplateError(permissionMessage(error.message));
    return (data as { id: string }).id;
}

export async function updateTemplate(templateId: string, draft: TemplateDraft): Promise<void> {
    assertUsableTemplate(draft);
    const { error } = await supabase()
        .from('message_templates')
        .update({ name: draft.name.trim(), content: draft.content, channel: draft.channel })
        .eq('id', templateId);
    if (error) throw new TemplateError(permissionMessage(error.message));
}

export async function duplicateTemplate(workspaceId: string, source: MessageTemplate): Promise<string> {
    return createTemplate(workspaceId, {
        name: `${source.name} (copy)`.slice(0, 80),
        content: source.content,
        channel: source.channel,
    });
}

export async function archiveTemplate(templateId: string, archived: boolean): Promise<void> {
    const { error } = await supabase()
        .from('message_templates')
        .update({ archived_at: archived ? new Date().toISOString() : null, is_default: archived ? false : undefined })
        .eq('id', templateId);
    if (error) throw new TemplateError(permissionMessage(error.message));
}

export async function deleteTemplate(templateId: string): Promise<void> {
    const { error } = await supabase().from('message_templates').delete().eq('id', templateId);
    if (error) throw new TemplateError(permissionMessage(error.message));
}

export async function setDefaultTemplate(workspaceId: string, templateId: string): Promise<void> {
    const { error } = await supabase().rpc('set_default_template', { p_workspace: workspaceId, p_template: templateId });
    if (error) throw new TemplateError(permissionMessage(error.message));
}
